import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { X } from "lucide-react";
import { Link } from "react-router-dom";
import { api, APIError } from "./api";
import { track } from "./analytics";
import type { User } from "./types";

interface AuthConfig {
  apple: { enabled: boolean; webClientID: string | null };
  google: { enabled: boolean; webClientID: string | null };
}

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  openSignIn(): void;
  refreshUser(): Promise<void>;
  signOut(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setDialogOpen] = useState(false);

  const refreshUser = useCallback(async () => {
    try {
      const response = await api<{ user: User }>("/v1/me");
      setUser(response.user);
    } catch (error) {
      if (error instanceof APIError && error.status === 401) setUser(null);
      else throw error;
    }
  }, []);

  useEffect(() => {
    refreshUser().catch(() => setUser(null)).finally(() => setIsLoading(false));
  }, [refreshUser]);

  const signOut = useCallback(async () => {
    try {
      await api("/v1/auth/sign-out", { method: "POST" });
    } finally {
      setUser(null);
    }
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    isLoading,
    openSignIn: () => {
      track("sign_in_opened");
      setDialogOpen(true);
    },
    refreshUser,
    signOut,
  }), [isLoading, refreshUser, signOut, user]);

  return (
    <AuthContext.Provider value={value}>
      {children}
      {isDialogOpen && (
        <SignInDialog
          onClose={() => setDialogOpen(false)}
          onSignedIn={(signedInUser) => {
            setUser(signedInUser);
            setDialogOpen(false);
          }}
        />
      )}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}

function SignInDialog({ onClose, onSignedIn }: { onClose(): void; onSignedIn(user: User): void }) {
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isWorking, setWorking] = useState(false);
  const googleButtonRef = useRef<HTMLDivElement>(null);

  const completeSignIn = useCallback(async (
    provider: "apple" | "google",
    identityToken: string,
    displayName?: string,
  ) => {
    setWorking(true);
    setError(null);
    try {
      const result = await api<{ user: User }>(`/v1/auth/${provider}`, {
        method: "POST",
        body: JSON.stringify({ identityToken, displayName }),
      });
      onSignedIn(result.user);
    } catch (signInError) {
      setError(signInError instanceof Error ? signInError.message : "Sign-in failed.");
    } finally {
      setWorking(false);
    }
  }, [onSignedIn]);

  useEffect(() => {
    api<AuthConfig>("/v1/auth/config")
      .then(setConfig)
      .catch((loadError) => setError(loadError instanceof Error ? loadError.message : "Could not load sign-in."));
  }, []);

  useEffect(() => {
    if (!config?.google.webClientID || !googleButtonRef.current) return;
    let cancelled = false;
    loadScript("https://accounts.google.com/gsi/client", "klist-google-auth").then(() => {
      if (cancelled || !window.google || !googleButtonRef.current || !config.google.webClientID) return;
      window.google.accounts.id.initialize({
        client_id: config.google.webClientID,
        callback: ({ credential }) => void completeSignIn("google", credential),
      });
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        type: "standard",
        theme: "filled_black",
        size: "large",
        shape: "pill",
        width: 320,
        text: "continue_with",
      });
    }).catch(() => setError("Google sign-in could not be loaded."));
    return () => {
      cancelled = true;
      window.google?.accounts.id.cancel();
    };
  }, [completeSignIn, config]);

  const signInWithApple = async () => {
    if (!config?.apple.webClientID) return;
    setWorking(true);
    setError(null);
    try {
      await loadScript(
        "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js",
        "klist-apple-auth",
      );
      if (!window.AppleID) throw new Error("Apple sign-in could not be loaded.");
      window.AppleID.auth.init({
        clientId: config.apple.webClientID,
        scope: "name email",
        redirectURI: `${window.location.origin}/auth/apple/callback`,
        state: randomValue(),
        nonce: randomValue(),
        usePopup: true,
      });
      const response = await window.AppleID.auth.signIn();
      const name = [response.user?.name?.firstName, response.user?.name?.lastName].filter(Boolean).join(" ");
      await completeSignIn("apple", response.authorization.id_token, name || undefined);
    } catch (appleError) {
      setError(appleError instanceof Error ? appleError.message : "Apple sign-in was cancelled.");
      setWorking(false);
    }
  };

  return (
    <div className="modal-layer" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="auth-dialog" role="dialog" aria-modal="true" aria-labelledby="sign-in-title">
        <button className="icon-button modal-close" onClick={onClose} aria-label="Close sign-in">
          <X size={19} />
        </button>
        <div className="brand-mark" aria-hidden="true">K</div>
        <p className="eyebrow">한곳에, 모든 이야기</p>
        <h1 id="sign-in-title">Keep every story with you.</h1>
        <p className="dialog-copy">Sign in once to sync your watch history, lists, and friends across iPhone and web.</p>

        <div className="auth-actions" aria-busy={isWorking}>
          <button
            className="apple-button"
            onClick={() => void signInWithApple()}
            disabled={isWorking || !config?.apple.webClientID}
          >
            <span className="apple-glyph" aria-hidden="true">●</span>
            Continue with Apple
          </button>
          {config?.google.webClientID && <div className="google-button" ref={googleButtonRef} />}
        </div>

        {config && !config.apple.webClientID && (
          <p className="auth-note">Apple sign-in is available in the iPhone app. Web access will appear after the Apple Services ID is connected.</p>
        )}
        {error && <p className="form-error" role="alert">{error}</p>}
        <p className="legal-copy">By continuing, you agree to the <Link to="/terms" onClick={onClose}>Terms</Link> and <Link to="/privacy" onClick={onClose}>Privacy Policy</Link>. We never receive your Apple or Google password.</p>
      </section>
    </div>
  );
}

function loadScript(source: string, id: string): Promise<void> {
  const existing = document.getElementById(id) as HTMLScriptElement | null;
  if (existing?.dataset.loaded === "true") return Promise.resolve();
  return new Promise((resolve, reject) => {
    const script = existing ?? document.createElement("script");
    script.id = id;
    script.src = source;
    script.async = true;
    script.onload = () => {
      script.dataset.loaded = "true";
      resolve();
    };
    script.onerror = () => reject(new Error("Sign-in service failed to load."));
    if (!existing) document.head.appendChild(script);
  });
}

function randomValue(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(24));
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}
